/**
 * Admin Panel - Pipedream Analytics API
 *
 * Zod schemas for tracking Pipedream plugin installs, execution frequency,
 * and cost across the platform. Used to prioritize native re-implementations.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

// ============================================================
// Common
// ============================================================

const PipedreamAnalyticsQuerySchema = z.object({
  days: z.coerce.number().min(1).max(365).optional().default(30)
});

// ============================================================
// Response Schemas
// ============================================================

const PipedreamAppStatsSchema = z.object({
  appSlug: z.string(),
  appName: z.string(),
  installs: z.number(),
  executions: z.number(),
  costTenThousandths: z.number(),
  lastExecuted: z.string().nullable()
});

const PipedreamAnalyticsTotalsSchema = z.object({
  totalApps: z.number(),
  totalInstalls: z.number(),
  totalExecutions: z.number(),
  totalCostTenThousandths: z.number(),
  activeOrgs: z.number()
});

const PipedreamActionStatsSchema = z.object({
  action: z.string(),
  appSlug: z.string(),
  executions: z.number(),
  lastExecuted: z.string().nullable()
});

/**
 * Result of triggering a background Pipedream job (import-apps / refresh-actions).
 * These endpoints kick off long-running work and return immediately.
 *
 * `status` is a literal, not an enum: "started" is the only outcome a 2xx can
 * carry. Failing to reach Pipedream used to answer 200 with `status: "error"`
 * and `success: false`, which the admin agent reads as a job that started. Both
 * of those are gone — a failure is now a 503. `success` is not restored as an
 * always-true field either: the `ApiSuccess` envelope already says that, and a
 * second copy inside the payload is what invited `if (!data.success)` in the
 * first place.
 */
const PipedreamJobStartResultSchema = z.object({
  status: z.literal("started"),
  message: z.string()
});

// ============================================================
// ZAdminPipedream - Endpoint Definitions
// ============================================================

export const ZAdminPipedream = {
  GetAnalytics: {
    method: "GET" as const,
    path: "/api/admin/pipedream/analytics",
    Params: PipedreamAnalyticsQuerySchema,
    Response: ApiSuccessSchema(
      z.object({
        totals: PipedreamAnalyticsTotalsSchema,
        leaderboard: z.array(PipedreamAppStatsSchema),
        topActions: z.array(PipedreamActionStatsSchema),
        days: z.number()
      })
    )
  },

  /**
   * Import all Pipedream apps and actions into the database (background job)
   * POST /api/admin/pipedream/import-apps
   */
  ImportApps: {
    method: "POST" as const,
    path: "/api/admin/pipedream/import-apps",
    Response: ApiSuccessSchema(PipedreamJobStartResultSchema)
  },

  /**
   * Refresh actions for all existing Pipedream tools (background job)
   * POST /api/admin/pipedream/refresh-actions
   */
  RefreshActions: {
    method: "POST" as const,
    path: "/api/admin/pipedream/refresh-actions",
    Response: ApiSuccessSchema(PipedreamJobStartResultSchema)
  }
} as const;
